import React, { useState } from 'react';
import { X, Droplets, Camera, Dumbbell, Pill, Moon, Utensils, TrendingUp, Zap, Coffee } from 'lucide-react';
import { C } from './dashboardConstants';

const REMINDER_ICONS = {
  water: { Icon: Droplets, color: '#4A90D9' },
  meal: { Icon: Camera, color: '#C67A5C' },
  exercise: { Icon: Dumbbell, color: '#6B9E8A' },
  supplement: { Icon: Pill, color: '#E5A84B' },
  sleep: { Icon: Moon, color: '#9B59B6' },
};

const COACH_ICONS = {
  recovery: { Icon: Zap, color: '#C67A5C' },
  macro: { Icon: TrendingUp, color: '#5A8C6A' },
  meal: { Icon: Utensils, color: '#C67A5C' },
  hydration: { Icon: Droplets, color: '#4A90D9' },
  wrapup: { Icon: Coffee, color: '#6B9E8A' },
  generic: { Icon: Utensils, color: '#9A9590' },
};

const MAX_VISIBLE = 5;

export const ProactiveCoachCard = ({ meals, dailyTotals, targets, waterCups, todayExercise, lastSleep, navigate, onRecipeSearch, macroGap, remaining, protRemaining }) => {
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('nuri_dismissed_nudges') || '[]'); } catch { return []; }
  });
  const [animatingOut, setAnimatingOut] = useState(null);

  const hour = new Date().getHours();
  const calTarget = targets.calories;
  const protTarget = targets.protein;
  const totalCal = Math.round(dailyTotals.calories);
  const totalProt = Math.round(dailyTotals.protein);
  const calLeft = Math.max(0, calTarget - totalCal);
  const protLeft = Math.max(0, protTarget - totalProt);
  const calPct = Math.round((totalCal / calTarget) * 100);
  const protPct = Math.round((totalProt / protTarget) * 100);
  const mealsNeeded = calLeft > 600 ? 2 : calLeft > 250 ? 1 : 0;
  const mealLabel = hour < 11 ? 'breakfast' : hour < 15 ? 'lunch' : hour < 18 ? 'snack' : 'dinner';
  const calOver = calTarget - totalCal < 0;

  const stack = [];

  // ── TIER 1: TIME-SENSITIVE REMINDERS ──
  if (hour >= 6 && hour < 10) {
    stack.push({ id: 'r-supplement', type: 'reminder', icon: 'supplement', msg: 'Take your morning supplements', priority: 0, onTap: () => navigate('/medications') });
    stack.push({ id: 'r-morning-water', type: 'reminder', icon: 'water', msg: 'Start with a glass of water', priority: 0, onTap: () => navigate('/hydration') });
  } else if (hour >= 10 && hour < 13) {
    stack.push({ id: 'r-midday-water', type: 'reminder', icon: 'water', msg: 'Stay hydrated — have a glass of water', priority: 0, onTap: () => navigate('/hydration') });
    if (meals.length === 0) stack.push({ id: 'r-log-lunch', type: 'reminder', icon: 'meal', msg: "Don't forget to log lunch", priority: 0, onTap: () => navigate('/log-meal') });
  } else if (hour >= 13 && hour < 17) {
    stack.push({ id: 'r-afternoon-water', type: 'reminder', icon: 'water', msg: 'Afternoon hydration check', priority: 0, onTap: () => navigate('/hydration') });
    if (!todayExercise) stack.push({ id: 'r-move', type: 'reminder', icon: 'exercise', msg: 'Time to move — even a short walk counts', priority: 0, onTap: () => navigate('/exercise') });
  } else if (hour >= 17 && hour < 21) {
    if (meals.length <= 2) stack.push({ id: 'r-dinner', type: 'reminder', icon: 'meal', msg: 'Log your dinner', priority: 0, onTap: () => navigate('/log-meal') });
    stack.push({ id: 'r-evening-water', type: 'reminder', icon: 'water', msg: 'Evening hydration', priority: 0, onTap: () => navigate('/hydration') });
  } else {
    stack.push({ id: 'r-sleep', type: 'reminder', icon: 'sleep', msg: 'Time to wind down for the night', priority: 0 });
  }

  // ── TIER 2: HEALTH-CRITICAL ──
  if (todayExercise && meals.length > 0 && protLeft > 20) {
    const elapsed = todayExercise.logged_minutes_ago || 60;
    if (elapsed < 120) {
      stack.push({
        id: 'hc-recovery', type: 'coach', coachIcon: 'recovery', priority: 1,
        msg: `Recovery window open — ${protLeft}g protein needed. A high-protein meal now maximizes muscle recovery.`,
        onTap: () => onRecipeSearch({ search: 'high protein recovery', category: 'snack', title: 'Recovery Meal Ideas' }),
      });
    }
  }

  if (macroGap?.show_card) {
    stack.push({
      id: 'hc-macro', type: 'coach', coachIcon: 'macro', priority: 1,
      msg: `${macroGap.remaining_prot}g protein left with ${macroGap.remaining_cal} cal to spend.`,
      onTap: () => navigate('/what-to-eat'),
    });
  }

  // ── TIER 3: CONTEXTUAL INTELLIGENCE ──
  if (hour >= 6 && hour < 10 && meals.length === 0) {
    const sleepNote = lastSleep?.quality === 'poor' ? ' After a rough night, protein-rich breakfast stabilizes energy.' : '';
    stack.push({
      id: 'ctx-morning', type: 'coach', coachIcon: 'meal', priority: 2,
      msg: `${calTarget} cal to work with today.${sleepNote} Start with a solid breakfast.`,
      onTap: () => onRecipeSearch({ search: 'high protein', category: 'breakfast', title: 'Breakfast Ideas' }),
    });
  }

  if (hour >= 10 && hour < 13 && meals.length === 1) {
    stack.push({
      id: 'ctx-late-morning', type: 'coach', coachIcon: 'meal', priority: 2,
      msg: `Breakfast logged (${totalCal} cal). ${calLeft} cal and ${protLeft}g protein left for ${mealsNeeded + 1} more meals.`,
      onTap: () => onRecipeSearch({ search: '', category: 'lunch', title: 'Lunch Ideas' }),
    });
  }

  if (hour >= 11 && hour < 14 && meals.length <= 1) {
    stack.push({
      id: 'ctx-lunch', type: 'coach', coachIcon: 'meal', priority: 2,
      msg: `Lunchtime — ${calLeft} cal remaining. ${protPct < 40 ? `Focus on protein (${protPct}% of target).` : 'Nice protein pace.'}`,
      onTap: () => onRecipeSearch({ search: '', category: 'lunch', title: 'Lunch Ideas' }),
    });
  }

  if (hour >= 14 && hour < 17 && waterCups < 6) {
    stack.push({
      id: 'ctx-hydration', type: 'coach', coachIcon: 'hydration', priority: 2,
      msg: `${waterCups}/10 cups today.${protLeft > 40 ? ` Also need ${protLeft}g protein — Greek yogurt covers both.` : ' Stay hydrated.'}`,
      onTap: () => navigate('/hydration'),
    });
  }

  if (hour >= 17 && hour < 21 && calLeft > 300) {
    stack.push({
      id: 'ctx-dinner', type: 'coach', coachIcon: 'meal', priority: 2,
      msg: `Dinner window — ${calLeft} cal and ${protLeft}g protein to go.`,
      onTap: () => onRecipeSearch({ search: 'high protein', category: 'dinner', title: 'Dinner Ideas' }),
    });
  }

  // ── TIER 4: GENTLE ENCOURAGEMENT ──
  if (calOver) {
    stack.push({
      id: 'g-over', type: 'coach', coachIcon: 'wrapup', priority: 3,
      msg: `${Math.abs(calTarget - totalCal)} cal over target — one day doesn't define your progress.`,
    });
  }

  if (hour >= 20 && meals.length >= 2) {
    const score = protPct >= 90 ? 'Crushed it' : protPct >= 70 ? 'Solid day' : 'Good effort';
    stack.push({
      id: 'g-evening', type: 'coach', coachIcon: 'wrapup', priority: 3,
      msg: `${score}! ${meals.length} meals, ${calPct}% cal, ${protPct}% protein.${waterCups < 8 ? ` Squeeze in ${10 - waterCups} more cups.` : ''}`,
    });
  }

  if (meals.length === 0) {
    stack.push({
      id: 'g-start', type: 'coach', coachIcon: 'meal', priority: 3,
      msg: 'Snap a photo of your first meal — Nuri breaks down every calorie and macro.',
      onTap: () => navigate('/log-meal'),
    });
  }

  if (meals.length > 0) {
    stack.push({
      id: 'g-progress', type: 'coach', coachIcon: 'generic', priority: 3,
      msg: `${totalProt}g protein so far. ${calLeft > 200 ? `Room for a solid ${mealLabel}.` : 'Almost there — finish strong!'}`,
      onTap: () => navigate('/what-to-eat'),
    });
  }

  // Filter dismissed, sort by priority, cap at MAX_VISIBLE
  const activeStack = stack
    .filter(item => !dismissedIds.includes(item.id))
    .sort((a, b) => a.priority - b.priority)
    .slice(0, MAX_VISIBLE);

  const handleDismiss = (itemId) => {
    setAnimatingOut(itemId);
    setTimeout(() => {
      const next = [...dismissedIds, itemId];
      setDismissedIds(next);
      sessionStorage.setItem('nuri_dismissed_nudges', JSON.stringify(next));
      setAnimatingOut(null);
    }, 250);
  };

  if (activeStack.length === 0) return null;

  return (
    <div className="nuri-reveal nuri-d2" data-testid="your-next-move-card" style={{
      background: C.cream, borderRadius: 20, padding: '16px 20px', marginBottom: 28,
    }}>
      <p style={{
        fontSize: 11, margin: '0 0 12px', letterSpacing: '0.5px', color: C.muted,
        fontFamily: 'var(--font-sans)', textTransform: 'uppercase',
      }}>
        Your next move
      </p>

      <div>
        {activeStack.map((item, idx) => {
          const isReminder = item.type === 'reminder';
          const iconDef = isReminder
            ? REMINDER_ICONS[item.icon]
            : COACH_ICONS[item.coachIcon] || COACH_ICONS.generic;
          const ItemIcon = iconDef?.Icon;
          const isSliding = animatingOut === item.id;

          return (
            <div key={item.id}>
              <div
                data-testid={`next-move-item-${idx}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0',
                  opacity: isSliding ? 0 : 1,
                  transform: isSliding ? 'translateX(40px)' : 'translateX(0)',
                  maxHeight: isSliding ? 0 : 200,
                  overflow: 'hidden',
                  transition: 'opacity 0.25s ease, transform 0.25s ease, max-height 0.25s ease',
                }}
              >
                {/* Left: icon + text (tappable) */}
                <div
                  onClick={item.onTap || undefined}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                    cursor: item.onTap ? 'pointer' : 'default',
                    minWidth: 0,
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    background: `${iconDef.color}12`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <ItemIcon style={{ width: 14, height: 14, color: iconDef.color }} />
                  </div>
                  <p style={{
                    fontSize: 13, margin: 0, color: C.text,
                    fontFamily: 'var(--font-sans)', lineHeight: 1.5,
                  }}>
                    {item.msg}
                  </p>
                </div>

                {/* Right: dismiss X */}
                <button
                  data-testid={`next-move-dismiss-${idx}`}
                  onClick={(e) => { e.stopPropagation(); handleDismiss(item.id); }}
                  style={{
                    width: 20, height: 20, borderRadius: 10, flexShrink: 0,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <X style={{ width: 14, height: 14, color: '#9A9590' }} />
                </button>
              </div>

              {/* Divider — between items, not after the last one */}
              {idx < activeStack.length - 1 && !isSliding && (
                <div style={{ height: 1, background: '#F0EBE6', margin: '0 4px' }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
